import { useCallback, useEffect, useState } from 'react'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// The browser fires beforeinstallprompt once, early. Capture it at module load so it isn't
// missed if the component using this hook mounts later.
let deferredPrompt: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((listener) => listener())

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    notify()
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    notify()
  })
}

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.matchMedia('(display-mode: fullscreen)').matches ||
  (navigator as any).standalone === true

// iPadOS reports itself as a Mac, so also check for touch support.
const isIos = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export function useInstallApp() {
  const [, rerender] = useState(0)

  useEffect(() => {
    const listener = () => rerender((n) => n + 1)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  const ios = isIos()
  const installed = isStandalone()

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return
    const event = deferredPrompt
    deferredPrompt = null
    await event.prompt()
    await event.userChoice
    notify()
  }, [])

  return {
    // Android/Chromium can install with one tap; iOS Safari needs manual steps.
    canInstall: !installed && !!deferredPrompt,
    needsManualInstall: !installed && ios,
    showInstall: !installed && (!!deferredPrompt || ios),
    promptInstall,
  }
}
